import { loadStripe, StripeCardElementChangeEvent } from "@stripe/stripe-js";
import {
  useModalOldContext,
  WSButton,
  WSFormOld,
  WSModalOld,
  WSStripeInput,
  WSStripeInputRef,
  WSText
} from "@wingspanhq/fe-component-library";
import { IClientUpdateRequest, IPaymentMethod } from "@wingspanhq/users";
import React, { useMemo, useRef, useState } from "react";
import { WSErrorMessage } from "../../../components/WSErrorMessage/WSErrorMessage";
import { useUserId } from "../../../query/hooks/helpers";
import {
  useMutationCreateClient,
  useMutationUpdateClient
} from "../../../query/users/mutations";
import { useClientQuery } from "../../../query/users/queries";
import { useIsStripe3dsCardSetupEnabled } from "../../../shared/hooks/useIsStripe3dsCardSetupEnabled";
import { confirmCardSetup3DS } from "../../../shared/utils/confirmCardSetup3DS";
import {
  addElementToArray,
  WSServiceError
} from "../../../utils/serviceHelper";
import { CHOOSE_ADD_PAYMENT_METHOD_FLOW_MODAL } from "./ChooseAddPaymentMethodFlowModal";

export const SAVED_PAYMENT_METHOD_CREDIT_CARD_MODAL =
  "SAVED_PAYMENT_METHOD_CREDIT_CARD_MODAL";

interface SavedPaymentMethodCreditCardFormProps {
  shouldSetDefaultPaymentMethod?: boolean;
  shouldSetSubscriptionPaymentMethod?: boolean;
  onFinish?: () => void;
}

export const SavedPaymentMethodCreditCardForm: React.FC<
  SavedPaymentMethodCreditCardFormProps
> = ({
  shouldSetDefaultPaymentMethod,
  shouldSetSubscriptionPaymentMethod,
  onFinish = () => {}
}) => {
  const { closeModal } = useModalOldContext();
  const stripeInput = useRef<WSStripeInputRef>(null);
  const [stripeComplete, setStripeComplete] = useState(false);
  const [cardError, setCardError] = useState<
    string | WSServiceError | undefined
  >();
  const [stripeInputState, setStripeInputState] = useState<
    StripeCardElementChangeEvent | undefined
  >();
  const stripePromise = useMemo(
    () => loadStripe(`${process.env.REACT_APP_STRIPE_KEY}`),
    []
  );
  const userId = useUserId();
  const isStripe3dsCardSetupEnabled = useIsStripe3dsCardSetupEnabled();
  const clientQuery = useClientQuery(userId);
  const [createClient, createClientMeta] = useMutationCreateClient(userId);
  const [updateClient, updateClientMeta] = useMutationUpdateClient(userId);

  const onFormSubmit = async () => {
    let token: string | undefined;
    setCardError(undefined);
    if (stripeInputState?.complete) {
      if (!clientQuery.data && clientQuery.error?.response?.status === 400) {
        createClient({ userId });
      }
      const result = await stripeInput?.current?.createPaymentMethod();
      if (result?.token) {
        token = result?.token.id;
      }

      if (token) {
        // Authenticate the card up-front — the actual charge runs off-session
        // during funds movement, where 3DS can no longer be answered.
        // This form has no mutation meta to absorb a rejected request, so
        // catch here instead of leaving the failure unreported.
        if (isStripe3dsCardSetupEnabled) {
          try {
            const setupResult = await confirmCardSetup3DS({
              stripePromise,
              payerId: userId,
              paymentMethodId: token
            });

            if (setupResult.error) {
              setCardError(setupResult.error);
              return;
            }
          } catch (error) {
            setCardError(error as WSServiceError);
            return;
          }
        }

        const request: IClientUpdateRequest = {
          clientId: userId,
          profile: {
            savedPaymentMethods: addElementToArray<IPaymentMethod>(
              clientQuery.data?.profile.savedPaymentMethods?.length || 0,
              { paymentMethodId: token }
            ),
            ...(shouldSetDefaultPaymentMethod
              ? {
                  defaultPaymentMethod: {
                    paymentMethodId: token
                  }
                }
              : {}),
            ...(shouldSetSubscriptionPaymentMethod
              ? {
                  subscriptionPaymentMethod: {
                    paymentMethodId: token
                  }
                }
              : {})
          }
        };
        await updateClient(request, {
          onSuccess: () => {
            closeModal(SAVED_PAYMENT_METHOD_CREDIT_CARD_MODAL);
            closeModal(CHOOSE_ADD_PAYMENT_METHOD_FLOW_MODAL);
            onFinish();
          }
        });
      }
    }
  };
  return (
    <WSFormOld onSubmit={onFormSubmit}>
      {({ formState }) => (
        <>
          <WSStripeInput
            ref={stripeInput}
            stripeInstance={stripePromise}
            onInputChange={event => {
              setStripeInputState(event);
              setStripeComplete(event.complete);
            }}
            name="creditCard"
            mb="XS"
          />
          <WSErrorMessage
            contextKey="BankCard"
            error={cardError || updateClientMeta.error}
          />
          <WSButton
            icon="lock"
            mt="XL"
            type="submit"
            disabled={!stripeComplete}
            fullWidth
            loading={formState.isSubmitting}
            name="submit"
          >
            Confirm payment method
          </WSButton>
        </>
      )}
    </WSFormOld>
  );
};

export const SavedPaymentMethodCreditCardModal = () => {
  return (
    <WSModalOld
      name={SAVED_PAYMENT_METHOD_CREDIT_CARD_MODAL}
      size="S"
      title="Add credit/debit card"
    >
      {modalProps => (
        <SavedPaymentMethodCreditCardForm
          shouldSetDefaultPaymentMethod={
            modalProps.shouldSetDefaultPaymentMethod
          }
          shouldSetSubscriptionPaymentMethod={
            modalProps.shouldSetSubscriptionPaymentMethod
          }
          onFinish={modalProps.onFinish}
        />
      )}
    </WSModalOld>
  );
};
